/**
 * SubmitForReviewDialog — Doc 21 (Approval Workflow), Fase 2.b.
 * Refactor Plan B (Doc 21 Fase 5++): el resolver del preview ahora vive
 * 100% en el backend (`SubmitPreviewService`). Este componente solo
 * llama `GET /projects/{id}/submit-preview` y renderiza la respuesta.
 *
 * Antes (legacy): el componente fetcheaba el snapshot completo, los
 * miembros y el catálogo de roles, y replicaba en TS ~700 LOC de
 * resolver (selección de lifecycle correcto, i18n por locale, cruce
 * con miembros, dedup, exclude_self, allow_skip_if_no_role_assignee).
 * Esa duplicación causó bugs cuando el snapshot tenía múltiples
 * lifecycles del tenant. Ahora: una sola fuente de verdad.
 */

import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  AlertCircle,
  ArrowRight,
  Check,
  GitBranch,
  Loader2,
  ShieldCheck,
  Users,
  X,
} from 'lucide-react'

import { ApiError } from '../../api/client'
import {
  fetchSubmitPreview,
  type StagePreview,
  type SubmitPreview,
} from '../../api/projects'
import { useAccessToken } from '../../auth/useAccessToken'

// ─── Component ──────────────────────────────────────────────────────────────
export interface SubmitForReviewDialogProps {
  open: boolean
  onClose: () => void
  /** ID del artifact que se va a someter a revisión. */
  artifactId: string
  /** ID del proyecto al que pertenece. Lo necesita el endpoint. */
  projectId: string
  /** `subphase_code` del artifact (ej. "phase-2.1"). Si null, el
   *  backend devuelve `source='empty'` (preview self-approve). */
  subphaseCode: string | null
  /** ID del usuario actual del proyecto. Lo usa el backend para marcar
   *  `is_self` en cada approver. Si null, no hay self badge. */
  currentUserId: string | null
  /** ID del autor del artifact. Lo usa el backend para resolver el
   *  pseudo-rol `rol-analyst-author` y aplicar `exclude_self`. */
  authorUserId: string | null
  /** Callback al confirmar. Devuelve la promesa para que el dialog
   *  pueda mostrar loading + cerrar al éxito. La función debe
   *  llamar a `submitForReview` y manejar invalidación de queries. */
  onSubmit: () => Promise<void>
}

export function SubmitForReviewDialog({
  open,
  onClose,
  projectId,
  subphaseCode,
  currentUserId,
  authorUserId,
  onSubmit,
}: SubmitForReviewDialogProps) {
  const { t } = useTranslation(['projects', 'errors'])
  const { accessToken } = useAccessToken()

  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // Un solo fetch — el backend resuelve TODO. Cache por (project,
  // subphase, author) porque el resolver depende de esos tres. El
  // `currentUserId` lo pasa por query string para que el backend
  // marque `is_self` correctamente, pero no entra al queryKey: si
  // cambia el current user, el JWT lo refleja y queremos refetch.
  const previewQuery = useQuery({
    queryKey: [
      'projects',
      projectId,
      'submit-preview',
      subphaseCode,
      authorUserId,
    ],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return fetchSubmitPreview(accessToken, projectId, {
        subphase_code: subphaseCode,
        author_user_id: authorUserId,
      })
    },
    enabled: open && !!accessToken,
    staleTime: 30_000,
  })

  // ESC + body scroll lock — bloqueamos cierre durante submit para
  // evitar perder feedback de error.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose()
    }
    window.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [open, submitting, onClose])

  if (!open) return null

  async function handleSubmit() {
    setSubmitting(true)
    setSubmitError(null)
    try {
      await onSubmit()
      onClose()
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null
      const fallback =
        err instanceof ApiError
          ? err.body?.message ?? err.message
          : err instanceof Error
            ? err.message
            : t('submit_dialog.errors.unknown')
      setSubmitError(
        code
          ? t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })
          : fallback,
      )
    } finally {
      setSubmitting(false)
    }
  }

  const preview = previewQuery.data ?? null
  const hasBlockingWarning =
    preview !== null &&
    (preview.error_code !== null ||
      preview.stages.some((s) => s.warning_code !== null && !s.will_be_skipped))

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-label={t('submit_dialog.title')}
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose()
      }}
    >
      <div className="mx-4 flex max-h-[90vh] w-full max-w-2xl flex-col rounded-md border border-line bg-surface shadow-lg">
        {/* Header */}
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
          <div className="flex items-center gap-2">
            <GitBranch className="w-4 h-4 text-brand" />
            <h2 className="font-display text-base font-semibold text-ink">
              {t('submit_dialog.title')}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-ink-subtle hover:text-ink disabled:opacity-50"
            aria-label={t('submit_dialog.close')}
          >
            <X className="w-4 h-4" />
          </button>
        </header>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {previewQuery.isLoading ? (
            <div className="flex items-center justify-center py-12 text-ink-subtle">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              <span className="text-sm">{t('submit_dialog.loading')}</span>
            </div>
          ) : previewQuery.error ? (
            <div className="rounded-md border border-critical/40 bg-critical-soft p-3 text-sm text-critical">
              {t('submit_dialog.errors.preview_failed')}
            </div>
          ) : preview ? (
            <PreviewBody preview={preview} currentUserId={currentUserId} />
          ) : null}

          {submitError ? (
            <div className="mt-4 rounded-md border border-critical/40 bg-critical-soft p-3 text-sm text-critical flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{submitError}</span>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-between gap-3 border-t border-line px-5 py-3">
          <p className="text-[11px] text-ink-subtle">
            {preview?.source === 'empty'
              ? t('submit_dialog.footer_empty_hint')
              : t('submit_dialog.footer_hint')}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-3 py-1.5 text-sm text-ink-muted hover:text-ink rounded-md disabled:opacity-50"
            >
              {t('submit_dialog.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={
                submitting ||
                previewQuery.isLoading ||
                !!previewQuery.error ||
                hasBlockingWarning
              }
              className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-brand text-canvas text-sm font-medium rounded-md hover:bg-brand-deep disabled:opacity-50"
            >
              {submitting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              )}
              {preview?.source === 'empty'
                ? t('submit_dialog.submit_self_approve')
                : t('submit_dialog.submit')}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}

// ─── Preview body ───────────────────────────────────────────────────────────
function PreviewBody({
  preview,
  currentUserId,
}: {
  preview: SubmitPreview
  currentUserId: string | null
}) {
  const { t } = useTranslation(['projects', 'errors'])

  // OT-0058 — el flujo declarado no se puede leer: el envío fallaría con
  // este mismo código, así que se dice aquí y no "sin aprobación".
  if (preview.error_code !== null) {
    return (
      <div className="rounded-md border border-critical/40 bg-critical-soft p-3 text-sm text-critical flex items-start gap-2">
        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
        <span>{t(`errors:${preview.error_code}`)}</span>
      </div>
    )
  }

  if (preview.source === 'empty') {
    return (
      <div className="rounded-md border border-official/30 bg-official-soft/40 px-4 py-4 flex items-start gap-3">
        <ShieldCheck className="w-5 h-5 text-official shrink-0 mt-0.5" />
        <div className="space-y-1">
          <p className="font-display text-sm font-semibold text-ink">
            {t('submit_dialog.empty.title')}
          </p>
          <p className="font-body text-xs text-ink-muted">
            {t('submit_dialog.empty.description')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-ink-subtle font-semibold">
            {t('submit_dialog.workflow_eyebrow')}
          </p>
          {preview.template_name ? (
            <h3 className="font-display text-sm font-semibold text-ink">
              {preview.template_name}
            </h3>
          ) : null}
          <p className="text-[11px] text-ink-subtle">
            {t(`submit_dialog.source.${preview.source}` as const)}
          </p>
        </div>
      </header>

      <ol className="space-y-2">
        {preview.stages.map((stage, idx) => (
          <li key={stage.order}>
            <StageRow
              stage={stage}
              isLast={idx === preview.stages.length - 1}
              currentUserId={currentUserId}
            />
          </li>
        ))}
      </ol>
    </div>
  )
}

function StageRow({
  stage,
  isLast,
  currentUserId,
}: {
  stage: StagePreview
  isLast: boolean
  /** El backend ya marca `is_self` en cada approver basándose en el
   *  current user del JWT. Mantenemos esta prop disponible por si
   *  algún caller necesita override en testing, pero no se usa para
   *  la decisión visual — confiamos en `approver.is_self`. */
  currentUserId: string | null
}) {
  const { t } = useTranslation('projects')
  // currentUserId queda como prop documentado pero el render usa
  // `approver.is_self` resuelto por el backend. Suppress unused.
  void currentUserId
  return (
    <div className="flex items-stretch gap-3">
      <div className="flex flex-col items-center gap-1">
        <div
          className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-mono font-semibold ${
            stage.will_be_skipped
              ? 'bg-surface-muted text-ink-subtle'
              : stage.warning_code !== null
                ? 'bg-critical-soft text-critical'
                : 'bg-brand-soft text-brand'
          }`}
        >
          {stage.order}
        </div>
        {!isLast ? <div className="flex-1 w-px bg-line" /> : null}
      </div>

      <div className="flex-1 min-w-0 pb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-display text-sm font-semibold text-ink">
            {stage.name}
          </span>
          <span className="text-[10px] text-ink-subtle font-mono">·</span>
          <span className="text-[10px] text-ink-subtle">
            {stage.min_approvals_label}
          </span>
          {stage.will_be_skipped ? (
            <span className="px-1.5 py-0.5 rounded bg-surface-muted text-[10px] uppercase tracking-wider text-ink-subtle">
              {t('submit_dialog.stage.skipped')}
            </span>
          ) : null}
        </div>

        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          {stage.required_roles.map((r) => (
            <span
              key={r.role_key}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-surface-muted text-[10px] text-ink-muted"
            >
              <Users className="w-2.5 h-2.5" />
              {r.role_label}
            </span>
          ))}
        </div>

        {stage.approvers.length > 0 ? (
          <ul className="mt-2 space-y-1">
            {stage.approvers.map((a, i) => (
              <li
                key={`${a.user_id ?? 'pseudo'}-${i}`}
                className="flex items-center gap-2 text-xs text-ink"
              >
                <ArrowRight className="w-3 h-3 text-ink-subtle" />
                <span className="font-medium">{a.display_name}</span>
                <span className="text-ink-subtle">·</span>
                <span className="text-ink-subtle">{a.role_label}</span>
                {a.is_self ? (
                  <span className="px-1.5 py-0.5 rounded bg-brand-soft text-[9px] uppercase tracking-wider text-brand">
                    {t('submit_dialog.stage.you')}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        ) : stage.will_be_skipped ? (
          <p className="mt-2 text-[11px] italic text-ink-subtle">
            {t('submit_dialog.stage.no_approvers_skipped')}
          </p>
        ) : stage.warning_code !== null ? (
          <p className="mt-2 text-[11px] text-critical flex items-center gap-1">
            <AlertCircle className="w-3 h-3 shrink-0" />
            {t(
              `submit_dialog.stage.warning_${stage.warning_code}` as
                | 'submit_dialog.stage.warning_NO_APPROVERS_AT_ALL'
                | 'submit_dialog.stage.warning_ONLY_AUTHOR_HAS_ROLE'
                | 'submit_dialog.stage.warning_NO_ACTIVE_LEADER'
                | 'submit_dialog.stage.warning_APPROVERS_PENDING_INVITATION',
            )}
          </p>
        ) : null}
      </div>
    </div>
  )
}
